<template>
  <transition name="remove-logo-dialog" appear>
    <div
      v-if="value"
      ref="overlay"
      class="remove-logo-dialog__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-logo-dialog-title"
      aria-describedby="remove-logo-dialog-body"
      tabindex="-1"
      data-testid="remove-logo-dialog"
      @mousedown.self="onCancel"
      @keydown.esc="onCancel"
    >
      <div class="remove-logo-dialog__card">
        <img class="remove-logo-dialog__pattern" :src="patternSrc" alt="" width="336" height="336" />
        <div class="remove-logo-dialog__header">
          <div class="remove-logo-dialog__featured-icon" aria-hidden="true">
            <Icon name="trash_03" class="remove-logo-dialog__trash-icon" />
          </div>
          <h2 id="remove-logo-dialog-title" class="remove-logo-dialog__title">Remove logo?</h2>
        </div>
        <p id="remove-logo-dialog-body" class="remove-logo-dialog__body">
          This removes your logo from the app. You can upload it again anytime.
        </p>
        <div class="remove-logo-dialog__actions">
          <StandardButton
            type="magenta"
            variant="secondary"
            size="normal"
            label="Cancel"
            :disabled="loading"
            data-testid="remove-logo-cancel"
            @click="onCancel"
          />
          <StandardButton
            type="red"
            variant="primary"
            size="normal"
            label="Remove logo"
            :disabled="loading"
            :loaderLeft="loading"
            data-testid="remove-logo-confirm"
            @click="onConfirm"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { nextTick, ref, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["input", "confirm"]);

const patternSrc = require("@components/assets/Attach_Background_pattern_decorative.svg");
const overlay = ref(null);

watch(
  () => props.value,
  async (open) => {
    if (!open) return;
    await nextTick();
    overlay.value?.focus();
  },
  { immediate: true }
);

function onCancel() {
  if (props.loading) return;
  emit("input", false);
}

function onConfirm() {
  if (props.loading) return;
  emit("confirm");
}
</script>

<style scoped>
.remove-logo-dialog__overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(13, 18, 28, 0.6);
  outline: none;
}

.remove-logo-dialog__card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
  background: #ffffff;
  border-radius: 12px;
  box-shadow:
    0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
}

.remove-logo-dialog__pattern {
  position: absolute;
  top: -120px;
  left: -120px;
  width: 336px;
  height: 336px;
  opacity: 0.5;
  pointer-events: none;
}

.remove-logo-dialog__header {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 24px 0;
}

.remove-logo-dialog__featured-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  box-sizing: border-box;
  background: #fee4e3;
  border: 1px solid #fbbab8;
  border-radius: 10px;
}

.remove-logo-dialog__trash-icon {
  width: 24px;
  height: 24px;
  color: #d92d20;
}

.remove-logo-dialog__title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.remove-logo-dialog__body {
  position: relative;
  margin: 0;
  padding: 0 24px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.remove-logo-dialog__actions {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 32px 24px 24px;
}

.remove-logo-dialog__actions > * {
  flex: 1;
}

.remove-logo-dialog-enter-active,
.remove-logo-dialog-leave-active {
  transition: opacity 0.15s ease;
}

.remove-logo-dialog-enter,
.remove-logo-dialog-leave-to {
  opacity: 0;
}
</style>
